import React, { useState } from 'react';

import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { VStack } from '../../../components/Common';
import { Icon } from '../../../components/Common/Icon';
import { ICON_ID } from '../../../components/Common/Icon/const';
import { type UsePaginationResponse } from '../../../components/Pagination/hooks/usePagination';
import { type Pds } from '../../../services/pds/types';
import { calcIndex } from '../../../utils/calcIndex';
import { formatDate } from '../../../utils/format';

const handleDownload = async (url: string, originalFileName: string) => {
	try {
		const response = await fetch(url);
		const blob = await response.blob();
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = downloadUrl;

		const urlParts = url.split('.');
		const extension = urlParts.length > 1 ? `.${urlParts[urlParts.length - 1].split('?')[0]}` : '';

		if (originalFileName.includes('.')) {
			link.download = originalFileName;
		} else {
			link.download = `${originalFileName}${extension}`;
		}

		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);
	} catch (error) {
		console.error('파일 다운로드 중 오류 발생:', error);
	}
};

const convertByteToMbAndKb = (byte: number) =>
	byte / 1024 > 1024 ? `${Math.floor(byte / 1024 / 1024)}Mb` : `${Math.floor(byte / 1024)}Kb`;

interface RowProps {
	data: Pds;
	idx: number;
	paging: UsePaginationResponse;
	isOpen: boolean;
	onClick: () => void;
}

const Row = ({ data, idx, paging, isOpen, onClick }: RowProps) => {
	const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);

	const handleFileDownload = async (fileId: number, url: string, fileName: string) => {
		setDownloadingFileId(fileId);
		try {
			await handleDownload(url, fileName);
		} finally {
			setDownloadingFileId(null);
		}
	};

	return (
		<React.Fragment>
			<Header
				onClick={(e) => {
					e.stopPropagation();
					onClick();
				}}
				$isOpen={isOpen}
			>
				<td>{calcIndex(paging, idx)}</td>
				<td className="title-cell">{data.title}</td>
				<td>{formatDate(new Date(data.createdAt), 'yyyy-MM-dd hh:mm:ss')}</td>
				<td
					onClick={(e) => {
						if (data.files.length === 1) {
							e.stopPropagation();
						}
					}}
					style={{ padding: '0.4rem' }}
				>
					{data.files.length === 1 && (
						<Button
							style={{ margin: 'auto' }}
							$buttonType="LINE"
							$height="auto"
							$padding="0.4rem"
							$color="PRIMARY"
							onClick={async () => {
								await handleFileDownload(data.files[0].id, data.files[0].url, data.files[0].name);
							}}
							disabled={downloadingFileId === data.files[0].id}
						>
							{downloadingFileId === data.files[0].id ? (
								<LoadingSpinner />
							) : (
								<Icon iconID={ICON_ID.FILE} width={'2.4rem'} height={'2.4rem'} $defaultColor="primary_600" />
							)}
						</Button>
					)}
				</td>
			</Header>
			<Data $isOpen={isOpen}>
				<td colSpan={6} className="not">
					<VStack className="inner">
						{data.content.length > 0 && (
							<div className="content-wrapper">
								<div className="inner__html" dangerouslySetInnerHTML={{ __html: data.content }}></div>
							</div>
						)}
						{data.files.length > 0 && (
							<div className="files-table-wrapper">
								<table className="files-table">
									<thead>
										<tr>
											<th>파일 이름</th>
											<th>파일 크기</th>
											<th>등록일</th>
											<th>다운로드</th>
										</tr>
									</thead>
									<tbody>
										{data.files.map((file) => (
											<tr key={'collapsible__row--' + idx + '--' + file.id}>
												<td>{file.name}</td>
												<td>{convertByteToMbAndKb(file.size)}</td>
												<td>{file.createdAt}</td>
												<td style={{ textAlign: 'center' }}>
													<Button
														style={{ display: 'inline-block' }}
														$buttonType="LINE"
														$height="auto"
														$padding="0.4rem"
														$color="PRIMARY"
														onClick={async (e) => {
															e.stopPropagation();
															await handleFileDownload(file.id, file.url, file.name);
														}}
														disabled={downloadingFileId === file.id}
														className="download-button"
													>
														{downloadingFileId === file.id ? (
															<LoadingSpinner />
														) : (
															<Icon
																iconID={ICON_ID.FILE}
																width={'2.4rem'}
																height={'2.4rem'}
																$defaultColor="primary_600"
															/>
														)}
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</VStack>
				</td>
			</Data>
		</React.Fragment>
	);
};

const Header = styled.tr<{ $isOpen: boolean }>`
	cursor: pointer;
	background-color: ${({ $isOpen, theme }) => ($isOpen ? theme.colors.primary[100] : 'transparent')};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};

	&:hover {
		background-color: ${({ $isOpen, theme }) => ($isOpen ? theme.colors.primary[100] : theme.colors.gray[100])};
	}

	td {
		padding: 1.2rem 1rem;
		vertical-align: middle;
	}

	.title-cell {
		width: 50%;
	}
`;

const Data = styled.tr<{ $isOpen: boolean }>`
	.inner {
		max-height: ${({ $isOpen }) => ($isOpen ? '100rem' : 0)};
		padding: ${({ $isOpen }) => ($isOpen ? '1rem' : '0 2rem')};
		overflow: hidden;
		background-color: ${({ theme }) => theme.colors.gray[100]};
		border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
	}

	.content-wrapper {
		background-color: white;
		border-radius: 8px;
		padding: 1.6rem;
		margin-bottom: 1rem;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	}

	.files-table-wrapper {
		background-color: white;
		border-radius: 8px;
		padding: 1rem;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		width: 100%;
		overflow-x: auto;
	}

	.inner__html {
		all: initial;
		font-family: inherit;
		font-size: 14px;
	}

	.files-table {
		width: 100%;
		border-collapse: collapse;
		margin-top: 1rem;
		table-layout: fixed;

		th,
		td {
			padding: 1rem;
			text-align: left;
			border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
			font-size: 14px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		th:nth-child(1),
		td:nth-child(1) {
			width: 40%;
		}
		
		th:nth-child(2),
		td:nth-child(2) {
			width: 15%;
		}
		
		th:nth-child(3),
		td:nth-child(3) {
			width: 25%;
		}
		
		th:nth-child(4),
		td:nth-child(4) {
			width: 20%;
			text-align: center;
		}

		th {
			font-weight: 600;
			background-color: ${({ theme }) => theme.colors.gray[100]};
			font-size: 14px;
		}

		.download-button {
			&:hover {
				background-color: ${({ theme }) => theme.colors.gray[100]};
				border-radius: 4px;
			}
		}
	}
`;

const LoadingSpinner = styled.div`
	width: 2.4rem;
	height: 2.4rem;
	border: 2px solid ${({ theme }) => theme.colors.gray[200]};
	border-top: 2px solid ${({ theme }) => theme.colors.primary[600]};
	border-radius: 50%;
	animation: spin 1s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
`;

export default Row;
